import React, { useEffect, useState } from "react";
import { Modal, Upload, Table, Select, Checkbox, Button, Icon, Popconfirm, Input, Spin } from "antd";
// antd v3 ships its base CSS separately - make sure this is imported once, e.g. in your app entry point
// import "antd/dist/antd.css";
import "./BulkAttachmentUpload.scss";
import { RemoveIcon, FileTypeBadge } from "../../common/BulkUploadFileType";
import UploadDragger_v2 from "./UploadDragger_v2";
import { randomUUID, alertMessage } from "../../common/Common";

const { Dragger } = Upload;
const { Option } = Select;

const MAX_SIZE_MB = 20;

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Existing docs come from the parent's own `documents` list - field names may vary
// by API, so this falls back across the common ones rather than assuming one shape.
// picture_url is the real field name your API actually returns.
function mapExistingDoc(doc, index) {
  const srcUrl = doc.src || doc.url || doc.file_url || doc.picture_url;
  const pictureObj = doc.picture_obj;
  const isDataUrl = (srcUrl || "").startsWith("data:");

  // For data: URLs (create flow) there's no filename embedded in the URL - the
  // real name/type live on the original File object (picture_obj). Fall back to
  // it so the row name and extension badge show the actual file, not base64.
  const urlName = !isDataUrl && srcUrl
    ? decodeURIComponent(srcUrl.split("?")[0].split("/").pop() || "")
    : "";

  return {
    uid: doc.id || doc.uid || `existing-${index}`,
    name:
      doc.name ||
      doc.file_name ||
      pictureObj?.name ||
      urlName ||
      doc.pic_title ||
      doc.pic_code ||
      "Document",
    size: doc.size ?? doc.document_size,
    tooLarge: false,
    isExisting: true, // internal flag - identifies already-saved docs in the list
    pic_title: doc.pic_title || doc.pic_code,
    pic_code: doc.pic_code,
    captured_at: doc.captured_at,
    ack_id: doc.ack_id,
    src: srcUrl,
    image_type: doc.image_type || pictureObj?.type,
    is_private: !!doc.is_private,
    is_osd: !!doc.is_osd,
    amount: doc.amount,
  };
}

export default function BulkAttachmentUpload({
  visible,
  onCancel,
  onUpload,
  onDocsChange,
  tags,
  isUploading,
  documents = [],
  isfromDoc = false,
  onDelete = () => {},
  defaultPicCode,
  defaultPicTitle,
  picSettings = [],
  isLocked,
  useTagsOnly = false,
  allowInternalToggle = false,
  hideDetailsColumn = false,
  fallbackPicCode = "OTHER ATTACHMENT",
}) {
  const [files, setFiles] = useState(documents || []);
  const [showActions] = useState(true);
  const [DocTypeOptions, setDocTypeOptions] = useState([]);

  const [previewFile, setPreviewFile] = useState(null);

  // True while any active (shown) doc is an OSD image - gates image-only
  // uploads, the Internal checkbox defaults, and the pic_code override.
  const hasOsdActive = files.some((f) => !!f.is_osd);

  // Config-driven "Internal" flag: when the doc type's picSettings entry lists
  // is_private, the Internal checkbox is forced ON (Documents flow).
  const isPrivateMandatory = (picCode) =>
    !!(picSettings || []).find((setting) => setting.pic_code === picCode)
      ?.is_private;

  const isInternalFree = () => !!allowInternalToggle;

  // Customs (allowInternalToggle): every doc type is editable + default checked.
  // Documents: OTHER ATTACHMENT and OSD are the doc types whose Internal flag stays editable.
  const isEditableInternalRow = (record) => {
    if (isInternalFree()) return true;
    const code = record.pic_code || defaultPicCode;
    return code === "OTHER ATTACHMENT" || !!record.is_osd;
  };

  const isImageFile = (entry) =>
    entry.image_type?.startsWith?.("image/") ||
    /\.(jpe?g|png|gif|webp|bmp)$/i.test(entry.name || "");

  const isPdfFile = (entry) =>
    entry.image_type === "application/pdf" || /\.pdf$/i.test(entry.name || "");

  useEffect(() => {
    // Extract unique document types from tags
    const uniqueDocTypes = Array.from(new Set(tags?.map((tag) => tag.pic_code)))
      .filter((code) => code !== "CASH RECEIPT" && !!code);

    // Customs (and similar) callers pass their own closed list of types and
    // should not pick up Documents defaults like signature / OTHER ATTACHMENT.
    if (useTagsOnly) {
      setDocTypeOptions(uniqueDocTypes);
      return;
    }

    const isTrue = (val) => val === true || val === "true";
    const hideCashReceipt = isTrue(isLocked)

    if (hideCashReceipt) {
      setDocTypeOptions(["signature", "OTHER ATTACHMENT", ...uniqueDocTypes]);
    } else {
      setDocTypeOptions(["signature", "OTHER ATTACHMENT", "CASH RECEIPT", ...uniqueDocTypes]);
    }
  }, [tags, isLocked, useTagsOnly]);

  // Seed the table with already-uploaded docs whenever the modal opens, and
  // clear back out when it closes (same behavior as the post-upload reset below).
  useEffect(() => {
    if (visible) {
      setFiles(documents.map(mapExistingDoc));
    } else {
      setFiles([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  // UploadDragger_v2 calls this as handleChange({ file }, { pic_title, pic_code })
  const handleChange = ({ file }, meta) => {
    const picCode = defaultPicCode || meta?.pic_code || undefined;
    const isSignature = picCode === "signature";

    setFiles((prev) => {
      // If any active (shown) doc is an OSD image, a newly uploaded file joins
      // that OSD context: its Internal checkbox becomes editable (is_osd) and
      // defaults to checked (is_private). Only image files are accepted then.
      const hasActiveOsd = prev.some((f) => !!f.is_osd);
      if (hasActiveOsd && !(file.type || "").startsWith("image/")) return prev;
      return [
        ...prev,
        {
          // UI-only fields, used for the table but stripped before syncing to the parent
          uid: file.uid,
          name: file.name,
          size: file.size,
          tooLarge: file.size > MAX_SIZE_MB * 1024 * 1024,
          internal: true,
          orderType: undefined,
          // Payload fields - match the parent's formattedDocuments destructure exactly
          picture_obj: file,
          captured_at: new Date().toISOString(),
          ack_id: `${randomUUID()}_n`,
          pic_title: defaultPicTitle || meta?.pic_title, // leave undefined if not provided
          pic_code: hasActiveOsd ? "OSD" : picCode, // undefined until user picks a type (unless bucket-scoped)
          is_osd: hasActiveOsd ? true : file.is_osd || false,
          src: URL.createObjectURL(file),
          image_type: isSignature
            ? "signature"
            : (file.type || "").startsWith("image/") || (file.type || "").toLowerCase() === "application/pdf"
              ? "normal"
              : file.image_type || file.type || file.name.split(".").pop(),
          picture_type: isSignature ? "signature" : "normal",
          // OSD context and Customs: default Internal checked for all types.
          // Documents: Internal defaults to false until a type is chosen; OTHER ATTACHMENT's
          // legacy default and mandatory types are still enforced once a type IS picked
          is_private: hasActiveOsd || allowInternalToggle
            ? true
            : picCode
              ? (picCode === "OTHER ATTACHMENT" || isPrivateMandatory(picCode))
              : false,
        },
      ];
    });
  };

  const removeFile = (uid) => setFiles((prev) => prev.filter((f) => f.uid !== uid));

  // Removing an already-saved doc should also hit the delete API (passed down
  // from Documents.jsx handleDelete) - newly staged files are only removed from
  // the local list.
  const handleRemove = (record) => {
    if (record?.isExisting && record.ack_id) {
      onDelete([record.ack_id], "modal");
    }
    removeFile(record.uid);
  };

  const updateFile = (uid, patch) =>
    setFiles((prev) => prev.map((f) => (f.uid === uid ? { ...f, ...patch } : f)));

  // Keep the parent's stagedDocs in sync on every add/remove/edit, so by the time
  // "Upload" is clicked, onUpload() (no args) reads a fully up-to-date list.
  // Existing (already-uploaded) docs are included too so edits (e.g. toggling
  // the "Internal" flag) are re-sent and persisted - the backend matches them
  // by ack_id and keeps the stored file.
// onDocsChange — this is where the real default lives, applied only at payload time
    useEffect(() => {
      if (!onDocsChange) return;
      // When an is_osd image is active in the list, every uploaded doc joins the
      // OSD bucket: send each pic_code as "OSD" in the payload.
      const hasOsdActive = files.some((f) => !!f.is_osd);
      const formattedDocs = files.map(({ captured_at, picture_obj, ack_id, pic_title, pic_code, src, image_type, is_private, size, amount }) => {
        const resolvedCode = hasOsdActive ? "OSD" : (pic_code || fallbackPicCode); // default only applied here, not shown in UI
        return {
          captured_at,
          picture_obj,
          ack_id,
          pic_title: pic_title || resolvedCode,
          pic_code: resolvedCode,
          src,
          image_type,
          is_private: !isInternalFree() && isPrivateMandatory(resolvedCode)
            ? true
            : !!is_private,
          document_size: size,
          amount: resolvedCode === "CASH RECEIPT" ? amount : undefined,
        };
      });
      onDocsChange(formattedDocs);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files]);

  const columns = [
    {
      title: "File",
      dataIndex: "name",
      key: "name",
      width: 250,
      render: (name) => (
        <span className="udm-file-cell">
          <FileTypeBadge fileName={name} />
          <span className="udm-file-name">{name}</span>
        </span>
      ),
    },
    ...(!isfromDoc
      ? [
          {
            title: "Size",
            dataIndex: "size",
            key: "size",
            width: 100,
            render: (size, record) => (record.tooLarge ? "Exceeds 20 MB" : formatSize(size)),
          },
        ]
      : []),
    {
      title: "Internal",
      dataIndex: "is_private",
      key: "is_private",
      align: "center",
      width: 90,
      render: (is_private, record) => (
        <Checkbox
          className="circle-checkbox"
          // Customs: editable for all types.
          // Documents: only OTHER ATTACHMENT and OSD rows are editable.
          disabled={!isEditableInternalRow(record)}
          checked={
            !isInternalFree() && isPrivateMandatory(record.pic_code || defaultPicCode)
              ? true
              : !!is_private
          }
          onChange={(e) => updateFile(record.uid, { is_private: e.target.checked })}
        />
      ),
    },
...(!isfromDoc
  ? [  // Document Type column — show placeholder, don't fake-select OTHER ATTACHMENT
    {
      title: "Document Type",
      dataIndex: "pic_title",
      key: "pic_title",
      width: 190,
      render: (value, record) => (
        <Select
          placeholder="Select Type"
          value={record.pic_code || undefined} // undefined -> antd shows placeholder instead of a phantom selection
          style={{ width: 160 }}
          optionLabelProp="value"
          onChange={(code) => {
            const isSignature = code === "signature";
            const forcedPrivate = allowInternalToggle
              ? true
              : isPrivateMandatory(code)
                ? true
                : code === "OTHER ATTACHMENT";

            updateFile(record.uid, {
              pic_title: code,
              pic_code: code,
              image_type: isSignature ? "signature" : "normal",
              picture_type: isSignature ? "signature" : "normal",
              is_private: forcedPrivate,
              amount: code === "CASH RECEIPT" ? record.amount : undefined,
            });
          }}
        >
          {DocTypeOptions.map((code) => (
            <Option key={code} value={code}>
              {code === "CASH RECEIPT" ? (
                <div style={{ display: "flex", flexDirection: "column", lineHeight: "1.2", padding: "4px 0" }}>
                  <span>CASH RECEIPT</span>
                  <span style={{ color: "red", fontSize: "10px" }}>Amount Require</span>
                </div>
              ) : (
                code
              )}
            </Option>
          ))}
        </Select>
      ),
    },
    ]
: []),
    {
      title: "Details",
      dataIndex: "amount",
      key: "amount",
      width: 160,
      render: (amount, record) => {
        const resolvedCode = record.pic_code || defaultPicCode || "OTHER ATTACHMENT";
        if (resolvedCode === "CASH RECEIPT") {
          const isInvalid = !record.amount || isNaN(parseFloat(record.amount)) || parseFloat(record.amount) <= 0;
          return (
            <div style={{ position: "relative" }}>
              <Input
                key={`cash-receipt-amount-${record.uid}`}
                type="number"
                placeholder="0.00"
                min={0}
                prefix={<Icon type="dollar" />}
                suffix={<span style={{ color: "#797979", fontSize: "12px", pointerEvents: "none" }}>USD</span>}
                value={record.amount || ""}
                onChange={(e) => updateFile(record.uid, { amount: e.target.value })}
                className={isInvalid ? "udm-input-error" : ""}
                style={{ width: 120 }}
              />
              {isInvalid && (
                <div style={{
                  position: "absolute",
                  left: 0,
                  top: "100%",
                  marginTop: "6px",
                  display: "flex",
                  alignItems: "center",
                  color: "red",
                  fontSize: "10px",
                  whiteSpace: "nowrap",
                  pointerEvents: "none"
                }}>
                  <span style={{
                    display: "inline-block",
                    width: "5px",
                    height: "5px",
                    borderRadius: "50%",
                    backgroundColor: "red",
                    marginRight: "4px"
                  }} />
                  Required
                </div>
              )}
            </div>
          );
        }
        return "-";
      },
    },
    // {
    //   title: "Order Type",
    //   dataIndex: "orderType",
    //   key: "orderType",
    //   render: (value, record) => (
    //     <Select
    //       placeholder="Select Type"
    //       value={value}
    //       style={{ width: 160 }}
    //       onChange={(val) => updateFile(record.uid, { orderType: val })}
    //     >
    //       {ORDER_TYPE_OPTIONS.map((opt) => (
    //         <Option key={opt} value={opt}>
    //           {opt}
    //         </Option>
    //       ))}
    //     </Select>
    //   ),
    // },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      width: 96,
      render: (_, record) => (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: "8px" }}>
          <Button type="link" className="udm-view-btn" onClick={() => setPreviewFile(record)} style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
            <Icon type="eye" style={{ fontSize: "18px" }} />
          </Button>
          <Popconfirm
            title="Are you sure you want to remove?"
            onConfirm={() => handleRemove(record)}
            onCancel={() => {}}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" className="udm-remove-btn" style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", padding: 0 }}>
              <RemoveIcon style={{ width: "18px", height: "18px" }} />
            </Button>
          </Popconfirm>
        </div>
      ),
    }
  ];

  return (
    <Modal
      visible={visible}
      onCancel={onCancel}
      footer={null}
      width={1040}
      className="udm-modal"
      title={
        <span className="udm-header">
          <Icon type="file-text" style={{ color: "#586FCC" }} />
          <span className="udm-header-title">Upload Documents</span>
        </span>
      }
    >
      <div className="udm-card">
        <h3 className="udm-card-title">Bulk Attachment Upload</h3>
      <Spin spinning={isUploading} tip="Uploading...">
        <UploadDragger_v2
          title="Drag & Drop Files Here"
          helperText={
            hasOsdActive
              ? `Drag and drop image files inside the dotted line or select the button below to upload. Only image files are accepted (JPG, PNG, GIF, WebP). Files can be a maximum of ${MAX_SIZE_MB} MB.`
              : `Drag and drop files inside the dotted line or select the button below to upload. Support for PDF, DOCX, XLSX, CSV, and images. Files can be a maximum of ${MAX_SIZE_MB} MB.`
          }
          showActions={showActions}
          handleChange={handleChange}
          isMultiple={true}
          accept={hasOsdActive ? "image/jpeg,image/jpg,image/png,image/gif,image/webp" : undefined}
        />
        </Spin>
        {files.length > 0 && (
          <>
            <div style={{ display: "flex", flexDirection: "column", gap: "4px", marginTop: "16px", marginBottom: "8px" }}>
              <div style={{ fontSize: "14px", color: "#000", fontWeight: "bold" }}>
                Documents
              </div>
              {!allowInternalToggle && (
                <div style={{ fontSize: "10px", color: "#7979798a" }}>
                  The Internal option is locked based on the Account Settings and cannot be modified, except for Other Attachments.
                </div>
              )}
            </div>
            <Table
              className="udm-table"
              columns={hideDetailsColumn ? columns.filter((col) => col.key !== "amount") : columns}
              dataSource={files}
              rowKey="uid"
              pagination={false}
              rowClassName={(record) => (record.tooLarge ? "udm-row--error" : "")}
            />
          </>
        )}
      </div>

      <div className="udm-footer">
        <Button className="udm-cancel-btn" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          type="primary"
          className="udm-upload-btn"
          disabled={files.length === 0}
          onClick={async () => {
            const hasInvalidAmount = files.some((file) => {
              const resolvedCode = file.pic_code || defaultPicCode || "OTHER ATTACHMENT";
              if (resolvedCode === "CASH RECEIPT") {
                return !file.amount || isNaN(parseFloat(file.amount)) || parseFloat(file.amount) <= 0;
              }
              return false;
            });
            if (hasInvalidAmount) {
              alertMessage("Please enter a valid amount for all Cash Receipt documents.", "error");
              return;
            }
            if (onUpload) {
              await onUpload();
            }
            // Parent closes the modal on success (visible=false resets files).
            // Clearing here as well would fire a second empty onDocsChange and
            // wipe create-flow staged docs that must survive until order save.
          }}
          loading={isUploading}
        >
          Upload
        </Button>
      </div>

      <Modal
        visible={!!previewFile}
        onCancel={() => setPreviewFile(null)}
        footer={null}
        title={
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingRight: "32px" }}>
            <span>{previewFile?.name}</span>
            {((previewFile?.pic_code || previewFile?.pic_title || defaultPicCode || "").toUpperCase() === "CASH RECEIPT") && (
              <div
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "8px",
                  background: "linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)",
                  border: "1px solid #a7f3d0",
                  padding: "4px 14px",
                  borderRadius: "20px",
                  fontSize: "13px",
                  color: "#047857",
                  fontWeight: 600,
                }}
              >
                <Icon type="dollar-circle" theme="filled" style={{ color: "#10b981", fontSize: "15px" }} />
                <span>Prepaid Amount:</span>
                <span style={{ color: "#065f46", fontWeight: 700 }}>
                  {(() => {
                    const val = previewFile?.amount;
                    if (val === undefined || val === null || val === "") return "N/A";
                    const strVal = val.toString().trim();
                    const num = parseFloat(strVal.replace(/[^0-9.-]+/g, ""));
                    if (isNaN(num)) return strVal;
                    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(num);
                  })()}
                </span>
              </div>
            )}
          </div>
        }
        className="udm-preview-modal"
        width={"70%"}
      >
        {previewFile && isImageFile(previewFile) && (
          <img src={previewFile.src} alt={previewFile.name} className="udm-preview-image" />
        )}
        {previewFile && isPdfFile(previewFile) && (
          <iframe src={previewFile.src} title={previewFile.name} className="udm-preview-frame" />
        )}
        {previewFile && !isImageFile(previewFile) && !isPdfFile(previewFile) && (
          <div className="udm-preview-fallback">
            <p>Preview isn't available for this file type.</p>
            <a href={previewFile.src} target="_blank" rel="noopener noreferrer">
              Open {previewFile.name}
            </a>
          </div>
        )}
      </Modal>
    </Modal>
  );
}